import { Link, useLocation, useNavigate } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, Phone } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { waLink, WhatsAppIcon, scrollToId } from "./index-helpers";

const NAV = [
  { label: "Sobre", id: "frei-lucas" },
  { label: "Padre Lucas Macieira", href: "/dom-frei-lucas-macieira" },
  { label: "Sobre Frei Lucas Macieira", href: "/sobre-frei-lucas-macieira" },
  { label: "Paróquia", id: "paroquia" },
  { label: "Cerimônias", id: "cerimonias" },
  { label: "Casamento Religioso", href: "/casamento-religioso" },
  { label: "Efeito Civil", href: "/casamento-religioso-com-efeito-civil" },
  { label: "Casamento para Divorciados", href: "/casamento-para-divorciados" },
  { label: "Casamentos", href: "/casamentos-realizados" },
  { label: "Parceiros", href: "/parceiros" },
  { label: "Blog", href: "/blog" },
  { label: "Contato", id: "contato" },
];

export default function Footer() {
  const { content } = useSite();
  const location = useLocation();
  const navigate = useNavigate();
  if (!content) return null;
  const c = content.contacts;

  const go = (item) => {
    if (item.href) return navigate(item.href);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(item.id), 500);
    } else scrollToId(item.id);
  };

  return (
    <footer id="contato" className="bg-olivedark grain relative pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <p className="font-serif text-2xl text-cream tracking-wide">{content.brand.name.toUpperCase()}</p>
            <p className="text-[11px] uppercase tracking-[0.3em] text-goldlight/80 mt-1">{content.brand.role}</p>
            <p className="mt-5 text-sm font-light text-cream/70 leading-relaxed" data-testid="footer-areas">
              {content.areas}
            </p>
            <p className="mt-6 font-script text-2xl text-goldlight" data-testid="footer-phrase">
              “{content.footer_phrase}”
            </p>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-goldlight mb-5">Contato</p>
            <div className="space-y-3">
              <a
                data-testid="footer-whatsapp"
                href={waLink(c.whatsapp_number, c.whatsapp_message)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-cream/80 hover:text-goldlight transition-colors text-sm"
              >
                <WhatsAppIcon className="w-4 h-4 text-whatsapp" /> {c.whatsapp_display}
              </a>
              <a
                data-testid="footer-email"
                href={`mailto:${c.email}`}
                className="flex items-center gap-3 text-cream/80 hover:text-goldlight transition-colors text-sm"
              >
                <Mail className="w-4 h-4 text-gold" /> {c.email}
              </a>
              <div className="flex items-center gap-4 pt-2">
                {c.instagram && (
                  <a data-testid="footer-instagram" href={c.instagram} target="_blank" rel="noopener noreferrer" className="text-cream/70 hover:text-goldlight transition-colors" aria-label="Instagram">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {c.facebook && (
                  <a data-testid="footer-facebook" href={c.facebook} target="_blank" rel="noopener noreferrer" className="text-cream/70 hover:text-goldlight transition-colors" aria-label="Facebook">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {c.youtube && (
                  <a data-testid="footer-youtube" href={c.youtube} target="_blank" rel="noopener noreferrer" className="text-cream/70 hover:text-goldlight transition-colors" aria-label="YouTube">
                    <Youtube className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-goldlight mb-5">Navegação</p>
            <nav className="grid grid-cols-2 gap-x-6 gap-y-2.5">
              {NAV.map((n) => (
                <button
                  key={n.label}
                  data-testid={`footer-link-${n.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
                  onClick={() => go(n)}
                  className="text-left text-sm text-cream/80 hover:text-goldlight transition-colors"
                >
                  {n.label}
                </button>
              ))}
              <Link to="/politica-de-privacidade" data-testid="footer-link-privacidade" className="text-sm text-cream/80 hover:text-goldlight transition-colors">
                Política de Privacidade
              </Link>
              <Link to="/politica-de-cookies" data-testid="footer-link-cookies" className="text-sm text-cream/80 hover:text-goldlight transition-colors">
                Política de Cookies
              </Link>
              <Link to="/termos-de-uso" data-testid="footer-link-termos" className="text-sm text-cream/80 hover:text-goldlight transition-colors">
                Termos de Uso
              </Link>
            </nav>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-cream/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-cream/50">
            © {new Date().getFullYear()} Padre Lucas Macieira — Celebrante de Casamentos. Belo Horizonte • Região Metropolitana • Minas Gerais
          </p>
          <Link to="/admin" data-testid="footer-admin-link" className="text-xs text-cream/40 hover:text-goldlight transition-colors">
            Painel do Administrador
          </Link>
        </div>
      </div>
    </footer>
  );
}
